import { z } from 'zod';
import { AgentPublicKeyB64 } from './agent-token.js';
import { IpAddress, IsoTime, ServerId } from './basics.js';
import { Capabilities } from './capabilities.js';
import { Command } from './command.js';
import { ConfigDocument, ConfigState } from './config.js';
import { GuardEvent } from './event.js';
import { GuardMetric } from './metric.js';
import { Role } from './settings.js';
/**
 * Knowledge doc section 10, method A. The token only joins the fleet; the permanent
 * credential is the key pair, so a leaked or expired token cannot affect servers
 * that already connected.
 */
export const EnrollRequest = z.object({
    token: z.string().min(20).max(256),
    hostname: z.string().min(1).max(255),
    /**
     * The address the server sees itself at, which is usually its private one and is
     * the useful thing to show in React. It is self-reported and therefore untrusted:
     * nothing may decide anything on it. The address the request actually came from is
     * what reaches the audit log.
     */
    ip: IpAddress,
    os: z.string().min(1).max(100),
    detectedRoles: z.array(Role),
    /** base64 Ed25519 public key. The private key never leaves the server. */
    publicKey: AgentPublicKeyB64,
    agentVersion: z.string().min(1).max(50),
});
/** `pending` when the enrollment token's approval mode requires an admin. */
export const EnrollResponse = z.object({
    serverId: ServerId,
    status: z.enum(['active', 'pending']),
});
export const ModuleHealth = z.object({
    ok: z.boolean(),
    detail: z.string().max(500).optional(),
});
/** Section 14: every 60 s. Three minutes of silence marks the server offline. */
export const Heartbeat = z.object({
    agentVersion: z.string().min(1).max(50),
    appliedConfigVersion: z.number().int().nonnegative(),
    capabilities: Capabilities.optional(),
    modules: z.record(z.string().max(64), ModuleHealth),
    sentAt: IsoTime,
});
/**
 * How long `GET /agent/sync` holds a request open (knowledge doc section 11:
 * "holds the request up to about 60 seconds").
 *
 * The agent's own request timeout has to be longer than this or it would cut the
 * hold short and reconnect every time, which is the opposite of what parking is
 * for — hence `SYNC_TIMEOUT_MS` beside it rather than the client's usual 30 s.
 */
export const SYNC_HOLD_MS = 55_000;
export const SYNC_TIMEOUT_MS = SYNC_HOLD_MS + 15_000;
/** What the agent asks with: the version it already has (section 15). */
export const SyncQuery = z.object({
    /** Zero from an agent that has never applied one, which is a real first request. */
    config_version: z.coerce.number().int().nonnegative().default(0),
});
/** Answer to the long poll. `config` is present only when newer than the agent's version. */
export const SyncResponse = z.object({
    config: ConfigDocument.optional(),
    commands: z.array(Command).max(100).default([]),
});
export const ConfigStatus = z.object({
    version: z.number().int().positive(),
    state: ConfigState,
    /**
     * Why it failed, or what drifted. Bounded, like every other agent-supplied text:
     * this is written to a column and read back in React, and an unbounded error
     * message from a failing module would otherwise land in both.
     */
    detail: z.string().max(2000).optional(),
});
/** Acceptance, and what the API now believes about this server. */
export const ConfigStatusReceipt = z.object({
    serverId: ServerId,
    version: z.number().int().positive(),
    state: ConfigState,
});
/** Batch caps stop one server from posting an unbounded payload. */
export const EventBatch = z.object({ events: z.array(GuardEvent).min(1).max(500) });
export const MetricBatch = z.object({ metrics: z.array(GuardMetric).min(1).max(5000) });
/**
 * What ingest answers with.
 *
 * `duplicates` is the number the API already had and ignored, which is the normal
 * case after a retry rather than an error (section 14, safe retries). The agent uses
 * it to tell "the API took this" from "the API took this twice", and an operator uses
 * it to tell a healthy replay from a buffer that is looping.
 */
export const IngestReceipt = z.object({
    received: z.number().int().nonnegative(),
    stored: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
});
/** Nothing to report back beyond acceptance, but the envelope always carries data. */
export const HeartbeatReceipt = z.object({
    serverId: ServerId,
    /** So an agent waiting for approval can say so in its own logs. */
    status: z.enum(['active', 'pending']),
});
//# sourceMappingURL=agent-api.js.map